import {Controller} from '@hotwired/stimulus';
import $ from 'jquery';
import {useModal} from '../js/stimulus_use/use-modal';

/* stimulusFetch: 'lazy' */
export default class extends Controller {
    static targets = [
        'formContainer',
    ];

    static values = {
        bookingId: Number,
    };

    // The booking's current slot, kept from the fragment payload. Declining doesn't change it,
    // but app_booking_edit reads date/start/end/medium up front, so they have to be sent.
    slot = {};

    submitting = false;

    connect() {
        useModal(this);
    }

    // Loads the two-step decline form (the DDT-3869 experience) for this booking, then opens the
    // modal. Called by decline-booking-link once it has set bookingIdValue on this outlet.
    showContent() {
        showPreloader();

        $.get(routes['app_booking_decline_form'].replace('__id__', this.bookingIdValue), response => {
            if (!response.status) {
                addAlert('danger', response.message || translations['profile.An unknown error occurred.']);

                return;
            }

            // Inject the server-rendered form; Stimulus connects reject-case-management (the shared
            // two-step state machine) and the tom-selects on insertion, exactly as the reject-case modal.
            // render() serialises to a Response object, so the HTML lives under result.content (the
            // reject-case modal reads it the same way), not result itself.
            this.formContainerTarget.innerHTML = response.result.content;

            // Raw values, already in the Y-m-d / H:i shape app_booking_edit parses.
            this.slot = response.slot;
            this.submitting = false;

            // reject-case-management#submitForm ends with `$(form).submit()` (jQuery) once step 2
            // validates; catch it here and turn it into the AJAX call the booking endpoint expects.
            // The listener MUST be bound through jQuery: jQuery's trigger only runs jQuery-bound
            // handlers, and its default action calls the native DOM form.submit() which never fires a
            // native 'submit' event — so a plain addEventListener would be bypassed and the form would
            // navigate to app_booking_edit as a full-page POST.
            const form = this.formContainerTarget.querySelector('form');

            if (form) {
                $(form).on('submit', event => this.submit(event));
            }

            this.open();
        }).fail(() => {
            addAlert('danger', translations['profile.An unknown error occurred.']);
        }).always(() => {
            hidePreloader();
        });
    }

    submit(event) {
        if (event) {
            event.preventDefault();
        }

        // The modal only opens through showContent(), which fills the slot; guard anyway so a
        // stray submit can't post the string "undefined" as the date. Also block double submits.
        if (this.submitting || !this.slot || !this.slot.date) {
            return;
        }

        const container = this.formContainerTarget;
        const checkedReason = container.querySelector('[data-reject-case-management-target="reasonGroup"] input:checked');

        // reject-case-management already blocks the submit when no reason is picked; guard anyway.
        if (!checkedReason) {
            return;
        }

        this.submitting = true;
        showPreloader();

        const message = this.fieldValue(container, '[data-reject-case-management-target="message"]');
        const reasonText = this.fieldValue(container, '[data-reject-case-management-target="freeText"]');
        const messageChoice = this.fieldValue(container, '[data-reject-case-management-target="messageChoice"]');
        const specialism = this.fieldValue(container, '[data-reject-case-management-target="specialism"]');
        const dontAllow = container.querySelector('input[type="checkbox"][name*="dont_allow_litigant_to_contact"]');

        const formData = new FormData();
        formData.append('action', 'decline');
        // Flat field names the decline branch of app_booking_edit reads (not the Symfony form prefix).
        formData.append('reason', checkedReason.value);
        formData.append('reason_description', reasonText);
        formData.append('message', message);
        formData.append('litigant_message_choice', messageChoice);
        formData.append('specialism_keyword', specialism);
        formData.append('dontAllowLitigantToContact', dontAllow && dontAllow.checked ? '1' : '0');
        // The slot is unchanged on decline, but the endpoint requires these fields.
        formData.append('date', this.slot.date);
        formData.append('start', this.slot.start);
        formData.append('end', this.slot.end);
        formData.append('medium', this.slot.medium);

        $.ajax({
            type: 'POST',
            url: routes['app_booking_edit'].replace('__id__', this.bookingIdValue),
            data: formData,
            processData: false,
            contentType: false,
        }).done(response => {
            if (response.status) {
                this.close();
                // Rebuilds the chat, which also clears the preloader.
                reloadChatAndUserInformation();
            } else {
                addAlert('danger', response.message || translations['profile.An unknown error occurred.']);

                this.submitting = false;
                hidePreloader();
            }
        }).fail(() => {
            addAlert('danger', translations['profile.An unknown error occurred.']);

            this.submitting = false;
            hidePreloader();
        });
    }

    // Reads a field value inside the injected form; tom-select mirrors its value onto the
    // underlying <select>, so element.value is correct for both plain and autocomplete fields.
    fieldValue(container, selector) {
        const element = container.querySelector(selector);

        return element ? element.value : '';
    }
}
